import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, FlatList, StyleSheet, Animated, RefreshControl, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import AppShell from '../../components/layout/AppShell';
import ScreenCommandHeader from '../../components/layout/ScreenCommandHeader';
import TopBar from '../../components/layout/TopBar';
import JobCard from '../../components/work/JobCard';
import ProductCard from '../../components/work/ProductCard';
import SupplyCard from '../../components/work/SupplyCard';
import QuoteCard from '../../components/work/QuoteCard';
import EmptyState from '../../components/work/EmptyState';
import JobFilters, { JobFilterOptions } from '../../components/work/JobFilters';
import SupplyFilters, { SupplyFilterOptions } from '../../components/work/SupplyFilters';
import FloatingActionButton from '../../components/common/FloatingActionButton';
import ThemedText from '../../components/common/ThemedText';
import CardSkeleton from '../../components/common/CardSkeleton';
import { WorkStackParamList } from '../../types/navigation';
import { WorkView } from '../../types/work';
import { useTheme } from '../../theme/ThemeContext';
import { useWork } from '../../context/WorkContext';
import { useAuth } from '../../context/AuthContext';

type WorkHomeNavigationProp = StackNavigationProp<WorkStackParamList, 'WorkHome'>;

const WorkHomeScreen: React.FC = () => {
  const navigation = useNavigation<WorkHomeNavigationProp>();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { jobs, myJobs, myQuotes, loadingJobs, loadingQuotes, hasMoreJobs, fetchJobs, fetchMyJobs, fetchMyQuotes } = useWork();
  const [selectedView, setSelectedView] = useState<WorkView>('all');
  const [selectedSubTab, setSelectedSubTab] = useState<'contracts' | 'supplies'>('contracts');
  const [selectedMarketTab, setSelectedMarketTab] = useState<'products'>('products');
  const [selectedMyTab, setSelectedMyTab] = useState<'jobs' | 'quotes'>('jobs');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<JobFilterOptions>({
    budgetRange: 'all',
    category: 'all',
    timeframe: 'all',
  });
  const [marketFilters, setMarketFilters] = useState<JobFilterOptions>({
    budgetRange: 'all',
    category: 'all',
    timeframe: 'all',
  });
  const [supplyFilters, setSupplyFilters] = useState<SupplyFilterOptions>({
    budgetRange: 'all',
    category: 'all',
    timeframe: 'all',
    state: 'all',
    district: 'all',
    city: 'all',
  });
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const [refreshing, setRefreshing] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const scrollY = useRef(new Animated.Value(0)).current;
  
  const headerTranslate = useMemo(() => {
    if (headerHeight === 0) {
      return 0;
    }
    // diffClamp tracks scroll DELTA, so scrolling up from anywhere in the list
    // immediately starts bringing the header back (not just when near the top)
    const clampedScrollY = Animated.diffClamp(scrollY, 0, headerHeight);
    return clampedScrollY.interpolate({
      inputRange: [0, headerHeight],
      outputRange: [0, -headerHeight],
      extrapolate: 'clamp',
    });
  }, [scrollY, headerHeight]);

  // Reset scroll position when switching tabs so the header is always visible
  // after a tab change (prevents header from staying hidden in the new tab)
  useEffect(() => {
    scrollY.setValue(0);
  }, [selectedView, selectedSubTab, selectedMyTab]);

  // Reload data when screen comes into focus (e.g., after adding/editing a job)
  useFocusEffect(
    React.useCallback(() => {
      loadData();
    }, [selectedView, selectedSubTab, selectedMyTab, selectedMarketTab])
  );

  // Fetch data on mount and when view changes
  useEffect(() => {
    loadData();
  }, [selectedView, selectedSubTab, selectedMyTab, selectedMarketTab]);

  const loadData = async () => {
    try {
      if (selectedView === 'all') {
        if (selectedSubTab === 'contracts') {
          await fetchJobs('contract', undefined, false);
        } else {
          // Supplies
          await fetchJobs('supply' as any, undefined, false);
        }
      } else if (selectedView === 'my-work') {
        if (selectedMyTab === 'jobs') {
          await fetchMyJobs();
        } else {
          await fetchMyQuotes();
        }
      } else if (selectedView === 'market') {
        // Products in market
        await fetchJobs('product', undefined, false);
      }
    } catch (error) {
      console.error('Error loading work data:', error);
    }
  };

  const handleLoadMore = async () => {
    if (hasMoreJobs && !loadingJobs) {
      if (selectedView === 'all') {
        const type = selectedSubTab === 'contracts' ? 'contract' : 'supply';
        await fetchJobs(type as any, undefined, true);
      } else if (selectedView === 'market') {
        await fetchJobs('product', undefined, true);
      }
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Compute categories relevant to the current view so filter chips stay meaningful
  const uniqueCategories = Array.from(
    new Set(
      selectedView === 'market'
        ? jobs.filter(j => j.type === 'product').map(j => j.category)
        : selectedView === 'all' && selectedSubTab === 'supplies'
          ? jobs.filter(j => (j as any).type === 'supply').map(j => j.category)
          : jobs.filter(j => j.type === 'contract').map(j => j.category)
    )
  );

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: -20,
        duration: 150,
        useNativeDriver: true,
      }),
    ]).start(() => {
      slideAnim.setValue(20);
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    });
  }, [selectedView]);

  const handleJobPress = (jobId: string, jobType?: 'contract' | 'product' | 'supply') => {
    if (jobType === 'product') {
      navigation.navigate('ProductDetail', { productId: jobId });
    } else if (jobType === 'supply') {
      navigation.navigate('SupplyDetail', { jobId });
    } else {
      navigation.navigate('ContractDetail', { jobId });
    }
  };

  const handleAddJobPress = () => {
    if (selectedView === 'market') {
      navigation.navigate('AddProduct');
    } else if (selectedView === 'all' && selectedSubTab === 'supplies') {
      navigation.navigate('AddSupply');
    } else {
      navigation.navigate('AddContract');
    }
  };

  const applyFilters = (jobsList: typeof jobs) => {
    // Determine which filter set to use based on current view
    const currentFilters =
      selectedView === 'market'
        ? marketFilters
        : selectedView === 'all' && selectedSubTab === 'supplies'
          ? supplyFilters
          : filters;

    return jobsList.filter(job => {
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = job.title.toLowerCase().includes(query);
        const matchesDescription = job.description.toLowerCase().includes(query);
        const matchesCategory = job.category.toLowerCase().includes(query);
        if (!matchesTitle && !matchesDescription && !matchesCategory) return false;
      }

      const amount = job.budget ?? job.price;
      if (currentFilters.budgetRange !== 'all' && amount !== undefined) {
        if (currentFilters.budgetRange === 'under-1000' && amount >= 1000) return false;
        if (currentFilters.budgetRange === '1000-5000' && (amount < 1000 || amount > 5000)) return false;
        if (currentFilters.budgetRange === 'over-5000' && amount <= 5000) return false;
      }

      if (currentFilters.category !== 'all' && job.category !== currentFilters.category) {
        return false;
      }

      if (currentFilters.timeframe !== 'all') {
        const now = new Date();
        const postedDate = new Date(job.createdAt);
        const diffTime = Math.abs(now.getTime() - postedDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (currentFilters.timeframe === 'today' && diffDays > 1) return false;
        if (currentFilters.timeframe === 'week' && diffDays > 7) return false;
        if (currentFilters.timeframe === 'month' && diffDays > 30) return false;
      }

      // Apply state and district filters for supplies
      if ('state' in currentFilters && 'district' in currentFilters && 'city' in currentFilters) {
        const supplyJob = job as any;
        const supplyCurrentFilters = currentFilters as SupplyFilterOptions;
        
        // State filter
        if (supplyCurrentFilters.state !== 'all' && supplyJob.state !== supplyCurrentFilters.state) {
          return false;
        }

        // District filter - only apply if state is also filtered
        if (supplyCurrentFilters.state !== 'all' && supplyCurrentFilters.district !== 'all' && supplyJob.district !== supplyCurrentFilters.district) {
          return false;
        }

        // City filter - search in workAddress or any city field
        if (supplyCurrentFilters.city !== 'all') {
          const cityQuery = supplyCurrentFilters.city.toLowerCase();
          const workAddress = (supplyJob.workAddress || '').toLowerCase();
          if (!workAddress.includes(cityQuery)) {
            return false;
          }
        }
      }

      return true;
    });
  };

  const renderAllJobs = () => {
    const filteredJobs = applyFilters(jobs);
    if (filteredJobs.length === 0 && !loadingJobs) {
      return <EmptyState icon="briefcase-outline" message="No jobs match your filters" />;
    }

    return (
      <Animated.FlatList
        data={filteredJobs}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => {
          if (item.type === 'product') {
            return <ProductCard product={item} onPress={() => handleJobPress(item.id, 'product')} />;
          } else if ((item as any).type === 'supply') {
            return <SupplyCard supply={item as any} onPress={() => handleJobPress(item.id, 'supply')} />;
          } else {
            return <JobCard job={item} onPress={() => handleJobPress(item.id, 'contract')} />;
          }
        }}
        contentContainerStyle={[styles.listContent, { paddingTop: headerHeight + 16 }]}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: true }
        )}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
        ListFooterComponent={() => {
          if (!hasMoreJobs || filteredJobs.length === 0) return null;
          
          return (
            <View style={styles.loadMoreContainer}>
              {loadingJobs ? (
                <ActivityIndicator size="small" color={theme.accent} />
              ) : (
                <TouchableOpacity
                  style={[styles.loadMoreButton, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
                  onPress={handleLoadMore}
                  activeOpacity={0.7}
                >
                  <ThemedText style={[styles.loadMoreText, { color: theme.accent }]}>Load More</ThemedText>
                  <Ionicons name="chevron-down" size={20} color={theme.accent} />
                </TouchableOpacity>
              )}
            </View>
          );
        }}
      />
    );
  };

  const renderMyWork = () => {
    if (selectedMyTab === 'jobs') {
      if (myJobs.length === 0) {
        return <EmptyState icon="briefcase-outline" message="You haven't posted any jobs" />;
      }

      return (
        <Animated.FlatList
          data={myJobs}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => {
            if (item.type === 'product') {
              return <ProductCard product={item} onPress={() => handleJobPress(item.id, 'product')} />;
            } else if ((item as any).type === 'supply') {
              return <SupplyCard supply={item as any} onPress={() => handleJobPress(item.id, 'supply')} />;
            } else {
              return <JobCard job={item} onPress={() => handleJobPress(item.id, 'contract')} />;
            }
          }}
          contentContainerStyle={[styles.listContent, { paddingTop: headerHeight + 16 }]}
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={16}
          onScroll={Animated.event(
            [{ nativeEvent: { contentOffset: { y: scrollY } } }],
            { useNativeDriver: true }
          )}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }
        />
      );
    } else {
      if (myQuotes.length === 0) {
        return <EmptyState icon="document-text-outline" message="You haven't sent any quotes yet" />;
      }

      return (
        <Animated.FlatList
          data={myQuotes}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <QuoteCard
              quote={item}
              jobTitle={item.job?.title || 'Unknown Job'}
              clientName={item.job?.createdBy?.name || 'Unknown Client'}
              onPress={() => handleJobPress(item.jobId, item.job?.type as 'contract' | 'product' | 'supply')}
            />
          )}
          contentContainerStyle={[styles.listContent, { paddingTop: headerHeight + 16 }]}
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={16}
          onScroll={Animated.event(
            [{ nativeEvent: { contentOffset: { y: scrollY } } }],
            { useNativeDriver: true }
          )}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }
        />
      );
    }
  };

  const renderMarket = () => {
    const products = jobs.filter(job => job.type === 'product');
    const filteredProducts = applyFilters(products);

    if (loadingJobs && products.length === 0) {
      return (
        <View style={styles.listContent}>
          {[...Array(3)].map((_, index) => (
            <CardSkeleton key={`market-skeleton-${index}`} />
          ))}
        </View>
      );
    }

    if (filteredProducts.length === 0 && !loadingJobs) {
      return <EmptyState icon="storefront-outline" message="No products in the market yet" />;
    }

    return (
      <Animated.FlatList
        data={filteredProducts}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <ProductCard product={item} onPress={() => handleJobPress(item.id, 'product')} />
        )}
        contentContainerStyle={[styles.listContent, { paddingTop: headerHeight + 16 }]}
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.5}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: true }
        )}
        ListFooterComponent={loadingJobs ? <ActivityIndicator size="small" color={theme.accent} style={{ marginVertical: 16 }} /> : null}
      />
    );
  };

  const renderContent = () => {
    switch (selectedView) {
      case 'all':
        return renderAllJobs();
      case 'my-work':
        return renderMyWork();
      case 'market':
        return renderMarket();
      default:
        return renderAllJobs();
    }
  };

  return (
    <AppShell title="Work" scrollable={false} showTopBar={false}>
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <Animated.View
          style={[styles.headerOverlay, { transform: [{ translateY: headerTranslate }] }]}
          onLayout={(event) => {
            const height = event.nativeEvent.layout.height;
            if (height !== headerHeight) {
              setHeaderHeight(height);
            }
          }}
        >
          <TopBar title="Work" showLogo={false} />
          <ScreenCommandHeader
          segments={[
            { value: 'all' as const, label: 'Requirements' },
            { value: 'my-work' as const, label: 'My Work' },
            { value: 'market' as const, label: 'Market' },
          ]}
          selectedValue={selectedView}
          onValueChange={setSelectedView}
          subTabs={selectedView === 'all' ? {
            options: [
              { value: 'contracts' as const, label: 'Contracts' },
              { value: 'supplies' as const, label: 'Supplies' },
            ],
            selectedValue: selectedSubTab,
            onChange: (value: any) => setSelectedSubTab(value),
          } : selectedView === 'my-work' ? {
            options: [
              { value: 'jobs' as const, label: 'My Jobs' },
              { value: 'quotes' as const, label: 'My Quotes' },
            ],
            selectedValue: selectedMyTab,
            onChange: (value: any) => setSelectedMyTab(value),
          } : undefined}
          searchConfig={selectedView === 'all' || selectedView === 'market' ? {
            value: searchQuery,
            onChange: setSearchQuery,
            placeholder: selectedView === 'all' 
              ? (selectedSubTab === 'contracts' ? 'Search Contracts' : 'Search Supplies')
              : 'Search Products',
            onClear: () => setSearchQuery(''),
          } : undefined}
          actionComponent={selectedView === 'all' || selectedView === 'market' ? (
            selectedView === 'all' && selectedSubTab === 'supplies' ? (
              <SupplyFilters
                filters={supplyFilters}
                onFiltersChange={setSupplyFilters}
                categories={uniqueCategories}
              />
            ) : selectedView === 'market' ? (
              <JobFilters
                filters={marketFilters}
                onFiltersChange={setMarketFilters}
                categories={uniqueCategories}
              />
            ) : (
              <JobFilters
                filters={filters}
                onFiltersChange={setFilters}
                categories={uniqueCategories}
              />
            )
          ) : undefined}
        />
        </Animated.View>

        <Animated.View
          style={[
            styles.content,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          {renderContent()}
        </Animated.View>

        <FloatingActionButton onPress={handleAddJobPress} />
      </View>
    </AppShell>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 10,
  },
  content: {
    flex: 1,
  },
  listContent: {
    paddingTop: 16,
    paddingBottom: 100,
  },
  loadMoreContainer: {
    paddingVertical: 20,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  loadMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  loadMoreText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default WorkHomeScreen;
